import { auth, db } from "../config/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { JournalEntryClass } from "@beyondrealityapp/core/journal/classes";
import {
  JournalEntryFirestoreType,
  JournalEntryFormType,
} from "@beyondrealityapp/core/journal/types";
import { JournalTemplateClass } from "@beyondrealityapp/core/journal/template/classes";
import {
  JournalTemplateFirestoreType,
  JournalTemplateFormType,
} from "@beyondrealityapp/core/journal/template/types";
import { JournalTemplateField } from "@beyondrealityapp/core/journal/template/constants";
import {
  Model,
  Operator,
  Order,
} from "@beyondrealityapp/core/shared/constants";
import { Filter } from "../utils/filter/classes";

export const journalEntryApi = createApi({
  reducerPath: "journalEntryApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["JournalEntry", "JournalTemplate"],
  endpoints: (builder) => ({
    fetchJournalEntries: builder.query({
      queryFn: async (filter: Filter) => {
        try {
          const journalEntries = await _fetchJournalEntries(filter);
          return { data: journalEntries };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["JournalEntry"],
    }),
    fetchJournalEntry: builder.query({
      queryFn: async (journalEntryId: string) => {
        try {
          const journalEntry = await _fetchJournalEntry(journalEntryId);
          return { data: journalEntry };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["JournalEntry"],
    }),
    createUpdateJournalEntry: builder.mutation({
      queryFn: async (journalEntryForm?: Partial<JournalEntryFormType>) => {
        try {
          const journalEntry = await _createUpdateJournalEntry(
            journalEntryForm
          );
          return { data: journalEntry };
        } catch (error) {
          console.log(error);
          return { error: error as Error };
        }
      },
      invalidatesTags: ["JournalEntry"],
    }),
    deleteJournalEntry: builder.mutation({
      queryFn: async (journalEntryId: string) => {
        try {
          await _deleteJournalEntry(journalEntryId);
          return { data: journalEntryId };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["JournalEntry"],
    }),
    createUpdateJournalTemplate: builder.mutation({
      queryFn: async (
        journalTemplateForm?: Partial<JournalTemplateFormType>
      ) => {
        try {
          const journalTemplate = await _createUpdateJournalTemplate(
            journalTemplateForm
          );
          return { data: journalTemplate };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["JournalTemplate"],
    }),
    fetchJournalTemplate: builder.query({
      queryFn: async (journalTemplateId?: string) => {
        if (!journalTemplateId) {
          return { error: new Error("No journal template ID provided") };
        }
        try {
          const journalTemplate = await _fetchJournalTemplate(
            journalTemplateId
          );
          return { data: journalTemplate };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["JournalTemplate"],
    }),
    fetchJournalTemplates: builder.query({
      queryFn: async () => {
        try {
          const journalTemplates = await _fetchJournalTemplates();
          return { data: journalTemplates };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["JournalTemplate"],
    }),
    deleteJournalTemplate: builder.mutation({
      queryFn: async (journalTemplateId?: string) => {
        if (!journalTemplateId) {
          return {
            error: new Error("No journal template ID provided"),
          };
        }
        try {
          await _deleteJournalTemplate(journalTemplateId);
          return { data: undefined };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "JournalTemplate", id: arg },
        "JournalTemplate",
      ],
    }),
  }),
});

export const {
  useFetchJournalEntriesQuery,
  useFetchJournalEntryQuery,
  useCreateUpdateJournalEntryMutation,
  useDeleteJournalEntryMutation,
  useCreateUpdateJournalTemplateMutation,
  useFetchJournalTemplateQuery,
  useFetchJournalTemplatesQuery,
  useDeleteJournalTemplateMutation,
} = journalEntryApi;

const _createUpdateJournalEntry = async (
  journalEntryForm?: Partial<JournalEntryFormType>
) => {
  let journalEntry = new JournalEntryClass({});
  if (journalEntryForm) {
    journalEntry = JournalEntryClass.fromForm(journalEntryForm);
  }
  journalEntry.init(auth);

  return new Promise<JournalEntryClass>((resolve, reject) => {
    setDoc(
      doc(db, Model.JOURNAL_ENTRIES, journalEntry.id),
      journalEntry.toFirestore()
    )
      .then(() => {
        resolve(journalEntry);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
const _deleteJournalEntry = async (journalEntryId: string) => {
  return new Promise<string>((resolve, reject) => {
    deleteDoc(doc(db, Model.JOURNAL_ENTRIES, journalEntryId))
      .then(() => {
        resolve(journalEntryId);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _fetchJournalEntry = async (journalEntryId: string) => {
  return new Promise<JournalEntryClass>((resolve, reject) => {
    getDoc(doc(db, Model.JOURNAL_ENTRIES, journalEntryId))
      .then((doc) => {
        if (doc.exists()) {
          resolve(
            JournalEntryClass.fromFirestore({
              ...(doc.data() as JournalEntryFirestoreType),
              id: doc.id,
            })
          );
        } else {
          reject(new Error("Journal entry not found"));
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _fetchJournalEntries = async (filter: Filter) => {
  const q = filter.createQuery(db);
  return new Promise<JournalEntryClass[]>((resolve, reject) => {
    getDocs(q)
      .then((querySnapshot) => {
        const journalEntries: JournalEntryClass[] = [];
        querySnapshot.forEach((doc) => {
          journalEntries.push(
            JournalEntryClass.fromFirestore({
              ...(doc.data() as JournalEntryFirestoreType),
              id: doc.id,
            })
          );
        });
        resolve(journalEntries);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _createUpdateJournalTemplate = async (
  journalTemplateForm?: Partial<JournalTemplateFormType>
) => {
  let journalTemplate = new JournalTemplateClass({});
  if (journalTemplateForm) {
    journalTemplate = JournalTemplateClass.fromForm(journalTemplateForm);
  }
  journalTemplate.init(auth);

  return new Promise<JournalTemplateClass>((resolve, reject) => {
    setDoc(
      doc(db, Model.JOURNAL_TEMPLATES, journalTemplate.id),
      journalTemplate.toFirestore()
    )
      .then(() => {
        resolve(journalTemplate);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _fetchJournalTemplate = async (journalTemplateId: string) => {
  return new Promise<JournalTemplateClass>((resolve, reject) => {
    getDoc(doc(db, Model.JOURNAL_TEMPLATES, journalTemplateId))
      .then((docSnapshot) => {
        if (docSnapshot.exists()) {
          resolve(
            JournalTemplateClass.fromFirestore(
              docSnapshot.data() as JournalTemplateFirestoreType
            )
          );
        } else {
          reject(new Error("Journal template not found"));
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _fetchJournalTemplates = async (userId?: string) => {
  return new Promise<JournalTemplateClass[]>((resolve, reject) => {
    let uid = "";
    if (userId) {
      uid = userId;
    }
    if (!userId && auth.currentUser) {
      uid = auth.currentUser.uid;
    }
    const q = query(
      collection(db, Model.JOURNAL_TEMPLATES),
      where(JournalTemplateField.UID, Operator.EQUAL_TO, uid),
      orderBy(JournalTemplateField.TITLE, Order.ASCENDING)
    );

    getDocs(q)
      .then((querySnapshot) => {
        const templates: JournalTemplateClass[] = [];
        querySnapshot.forEach((doc) => {
          templates.push(
            JournalTemplateClass.fromFirestore(
              doc.data() as JournalTemplateFirestoreType
            )
          );
        });
        resolve(templates);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _deleteJournalTemplate = async (journalTemplateId: string) => {
  return new Promise<void>((resolve, reject) => {
    deleteDoc(doc(db, Model.JOURNAL_TEMPLATES, journalTemplateId))
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error as Error);
      });
  });
};
